import {
  STREAMLAIN_DISCLOSURE_ENTER_TIMING,
  STREAMLAIN_DISCLOSURE_EXIT_TIMING,
  streamLainDisclosureOverlayKeyframesCss,
} from '@blocksuite/affine-shared/utils';

/**
 * StreamLain dropdown panel chrome — framework-agnostic DOM utilities for the
 * signature popover look: overlay panel with an SVG tail (Kerbe) that points
 * at the trigger element, plus the grow-out-of-the-tail enter/exit animation.
 *
 * Consumed by the Lit-based AI chat dropdowns (via
 * `blocksuite/ai/components/ai-chat-input/dropdown-menu-chrome.ts`) and by
 * React/Radix popovers (via `./radix-chrome.tsx`).
 *
 * The tail supports all four trigger sides. The side attribute describes where
 * the panel sits relative to the trigger:
 * - `top` (default) / `bottom`: tail on the bottom/top edge, horizontal offset
 *   via `--streamlain-chat-input-dropdown-tail-left`.
 * - `right` / `left`: tail on the left/right edge, vertical offset via
 *   `--streamlain-chat-input-dropdown-tail-top`.
 */

const DROPDOWN_MENU_STYLE_ID = 'streamlain-chat-input-dropdown-menu-style';
const DROPDOWN_MENU_ENTER_KEYFRAME =
  'streamlain-chat-input-dropdown-menu-enter';
const DROPDOWN_MENU_EXIT_KEYFRAME = 'streamlain-chat-input-dropdown-menu-exit';
const DROPDOWN_MENU_ENTER_RIGHT_KEYFRAME =
  'streamlain-chat-input-dropdown-menu-enter-right';
const DROPDOWN_MENU_EXIT_RIGHT_KEYFRAME =
  'streamlain-chat-input-dropdown-menu-exit-right';
const DROPDOWN_MENU_ENTER_LEFT_KEYFRAME =
  'streamlain-chat-input-dropdown-menu-enter-left';
const DROPDOWN_MENU_EXIT_LEFT_KEYFRAME =
  'streamlain-chat-input-dropdown-menu-exit-left';
export const STREAMLAIN_DROPDOWN_MENU_CLASS =
  'streamlain-chat-input-dropdown-menu';
export const STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS =
  'streamlain-chat-input-dropdown-menu-closing';
export const STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX =
  'var(--streamlain-z-index-dropdown-panel, 10020)';
const DROPDOWN_MENU_TAIL_CLASS = 'streamlain-chat-input-dropdown-menu-tail';
const DROPDOWN_MENU_TAIL_FILL_CLASS =
  'streamlain-chat-input-dropdown-menu-tail-fill';
const DROPDOWN_MENU_TAIL_BORDER_CLASS =
  'streamlain-chat-input-dropdown-menu-tail-border';
const DROPDOWN_MENU_TAIL_LEFT_VAR =
  '--streamlain-chat-input-dropdown-tail-left';
const DROPDOWN_MENU_TAIL_TOP_VAR = '--streamlain-chat-input-dropdown-tail-top';
const DROPDOWN_MENU_SIDE_ATTR = 'data-streamlain-dropdown-side';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const DROPDOWN_MENU_TAIL_HALF_WIDTH = 15;
const DROPDOWN_MENU_TAIL_WIDTH = DROPDOWN_MENU_TAIL_HALF_WIDTH * 2;
const DROPDOWN_MENU_TAIL_STROKE_WIDTH = 0.5;
const DROPDOWN_MENU_TAIL_HEIGHT = 15;
const DROPDOWN_MENU_TAIL_PANEL_OVERLAP = 0.25;
// Fallback clearance for panels whose radius cannot be read from computed
// styles. Runtime tail clamping uses the actual edge corner radius whenever
// possible so the curve never bleeds into rounded panel corners.
const DROPDOWN_MENU_TAIL_CORNER_CLEARANCE = 8;
const DROPDOWN_MENU_CLOSE_FALLBACK_MS = 440;
export const STREAMLAIN_DROPDOWN_MENU_TRIGGER_GAP = 0.5;
export function getStreamLainDropdownMenuStartAlignOffset(triggerSize: number) {
  const tailMinInset =
    DROPDOWN_MENU_TAIL_HALF_WIDTH +
    DROPDOWN_MENU_TAIL_STROKE_WIDTH / 2 +
    DROPDOWN_MENU_TAIL_CORNER_CLEARANCE;

  return Math.max(0, tailMinInset - triggerSize / 2);
}
export const STREAMLAIN_DROPDOWN_MENU_SIDE_START_ALIGN_OFFSET =
  getStreamLainDropdownMenuStartAlignOffset(30);
export const STREAMLAIN_DROPDOWN_MENU_OFFSET =
  DROPDOWN_MENU_TAIL_HEIGHT -
  DROPDOWN_MENU_TAIL_PANEL_OVERLAP +
  STREAMLAIN_DROPDOWN_MENU_TRIGGER_GAP;

const STREAMLAIN_MENU_THEME_VARS = [
  '--streamlain-v2-layer-background-overlayPanel',
  '--streamlain-v2-layer-insideBorder-border',
  '--streamlain-v2-layer-background-hoverOverlay',
  '--streamlain-v2-layer-background-secondary',
  '--streamlain-v2-layer-pureWhite',
  '--streamlain-v2-text-primary',
  '--streamlain-v2-text-secondary',
  '--streamlain-v2-button-primary',
  '--streamlain-v2-icon-primary',
  '--streamlain-v2-icon-secondary',
  '--streamlain-text-primary-color',
  '--streamlain-text-secondary-color',
  '--streamlain-hover-color',
  '--streamlain-overlay-panel-shadow',
] as const;

export function applyStreamLainMenuTheme(menuElement: HTMLElement) {
  const rootStyles = getComputedStyle(document.documentElement);
  for (const variableName of STREAMLAIN_MENU_THEME_VARS) {
    const value = rootStyles.getPropertyValue(variableName).trim();
    if (value) menuElement.style.setProperty(variableName, value);
  }
}

export function getStreamLainDropdownMenuChromeCss() {
  return `
	    .${STREAMLAIN_DROPDOWN_MENU_CLASS},
	    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS} {
	      position: relative;
	      box-sizing: border-box;
	      border: 0.5px solid var(--streamlain-v2-layer-insideBorder-border);
	      background: var(--streamlain-v2-layer-background-overlayPanel);
	      background-clip: padding-box;
	      transform-origin: var(${DROPDOWN_MENU_TAIL_LEFT_VAR}, 50%) calc(100% + 10px);
	      will-change: opacity, transform;
	    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='bottom'],
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='bottom'] {
      transform-origin: var(${DROPDOWN_MENU_TAIL_LEFT_VAR}, 50%) -10px;
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'],
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'] {
      transform-origin: -10px var(${DROPDOWN_MENU_TAIL_TOP_VAR}, 50%);
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'],
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'] {
      transform-origin: calc(100% + 10px) var(${DROPDOWN_MENU_TAIL_TOP_VAR}, 50%);
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS} {
      animation: ${DROPDOWN_MENU_ENTER_KEYFRAME} ${STREAMLAIN_DISCLOSURE_ENTER_TIMING};
    }

    /*
     * Attribute-qualified duplicate keeps the chrome enter animation ahead of
     * host content animations of equal single-class specificity (e.g. the
     * shared Radix menu content animation).
     */
    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}] {
      animation: ${DROPDOWN_MENU_ENTER_KEYFRAME} ${STREAMLAIN_DISCLOSURE_ENTER_TIMING};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'] {
      animation-name: ${DROPDOWN_MENU_ENTER_RIGHT_KEYFRAME};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'] {
      animation-name: ${DROPDOWN_MENU_ENTER_LEFT_KEYFRAME};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS} {
      animation: ${DROPDOWN_MENU_EXIT_KEYFRAME} ${STREAMLAIN_DISCLOSURE_EXIT_TIMING};
      pointer-events: none;
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'] {
      animation-name: ${DROPDOWN_MENU_EXIT_RIGHT_KEYFRAME};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'] {
      animation-name: ${DROPDOWN_MENU_EXIT_LEFT_KEYFRAME};
    }

    /*
     * Radix-driven panels close via [data-state='closed'] instead of the
     * imperative closing class; Radix delays unmount until this animation
     * finishes. Attribute selectors keep these rules more specific than the
     * shared menu content animation classes.
     */
    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}][data-state='closed'] {
      animation: ${DROPDOWN_MENU_EXIT_KEYFRAME} ${STREAMLAIN_DISCLOSURE_EXIT_TIMING};
      pointer-events: none;
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'][data-state='closed'] {
      animation-name: ${DROPDOWN_MENU_EXIT_RIGHT_KEYFRAME};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'][data-state='closed'] {
      animation-name: ${DROPDOWN_MENU_EXIT_LEFT_KEYFRAME};
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='bottom'] .${DROPDOWN_MENU_TAIL_CLASS},
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='bottom'] .${DROPDOWN_MENU_TAIL_CLASS} {
      top: auto;
      bottom: calc(100% + 0.25px);
      transform: translateX(-50%) scaleY(-1);
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'] .${DROPDOWN_MENU_TAIL_CLASS},
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='right'] .${DROPDOWN_MENU_TAIL_CLASS} {
      left: 0;
      right: auto;
      bottom: auto;
      top: var(${DROPDOWN_MENU_TAIL_TOP_VAR}, 50%);
      width: ${DROPDOWN_MENU_TAIL_HEIGHT}px;
      height: ${DROPDOWN_MENU_TAIL_WIDTH}px;
      transform: translate(calc(-100% + ${DROPDOWN_MENU_TAIL_PANEL_OVERLAP}px), -50%);
    }

    .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'] .${DROPDOWN_MENU_TAIL_CLASS},
    .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}='left'] .${DROPDOWN_MENU_TAIL_CLASS} {
      left: 100%;
      right: auto;
      bottom: auto;
      top: var(${DROPDOWN_MENU_TAIL_TOP_VAR}, 50%);
      width: ${DROPDOWN_MENU_TAIL_HEIGHT}px;
      height: ${DROPDOWN_MENU_TAIL_WIDTH}px;
      transform: translate(-${DROPDOWN_MENU_TAIL_PANEL_OVERLAP}px, -50%);
    }

    .${DROPDOWN_MENU_TAIL_CLASS} {
      position: absolute;
      left: var(${DROPDOWN_MENU_TAIL_LEFT_VAR}, 50%);
      top: calc(100% - 0.25px);
      width: ${DROPDOWN_MENU_TAIL_WIDTH}px;
      height: ${DROPDOWN_MENU_TAIL_HEIGHT}px;
      transform: translateX(-50%);
      overflow: visible;
      pointer-events: auto;
    }

    .${DROPDOWN_MENU_TAIL_CLASS}::before {
      content: '';
      position: absolute;
      inset: -${STREAMLAIN_DROPDOWN_MENU_TRIGGER_GAP}px;
    }

    .${DROPDOWN_MENU_TAIL_CLASS} svg {
      display: block;
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    .${DROPDOWN_MENU_TAIL_FILL_CLASS} {
      fill: var(--streamlain-v2-layer-background-overlayPanel);
      stroke: none;
    }

    .${DROPDOWN_MENU_TAIL_BORDER_CLASS} {
      fill: none;
      stroke: var(--streamlain-v2-layer-insideBorder-border);
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 0.5px;
      vector-effect: non-scaling-stroke;
    }

    ${streamLainDisclosureOverlayKeyframesCss({
      enter: DROPDOWN_MENU_ENTER_KEYFRAME,
      enterLeft: DROPDOWN_MENU_ENTER_LEFT_KEYFRAME,
      enterRight: DROPDOWN_MENU_ENTER_RIGHT_KEYFRAME,
      exit: DROPDOWN_MENU_EXIT_KEYFRAME,
      exitLeft: DROPDOWN_MENU_EXIT_LEFT_KEYFRAME,
      exitRight: DROPDOWN_MENU_EXIT_RIGHT_KEYFRAME,
    })}

    @media (prefers-reduced-motion: reduce) {
      .${STREAMLAIN_DROPDOWN_MENU_CLASS},
      .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS},
      .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}],
      .${STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}],
      .${STREAMLAIN_DROPDOWN_MENU_CLASS}[${DROPDOWN_MENU_SIDE_ATTR}][data-state='closed'] {
        animation: none;
      }
    }
  `;
}

export function ensureStreamLainDropdownMenuChromeStyle() {
  const style =
    document.getElementById(DROPDOWN_MENU_STYLE_ID) ??
    document.createElement('style');
  style.id = DROPDOWN_MENU_STYLE_ID;
  style.textContent = getStreamLainDropdownMenuChromeCss();
  if (!style.isConnected) {
    document.head.append(style);
  }
}

export type StreamLainDropdownPanelSide = 'left' | 'right';

export interface StreamLainDropdownPanelOptions {
  /**
   * Forces a horizontal panel side (panel sits left/right of the trigger, the
   * tail points sideways at it). Without this option the tail keeps the
   * original vertical auto-placement (top/bottom).
   */
  side?: StreamLainDropdownPanelSide;
}

export interface StreamLainDropdownAnchor {
  getBoundingClientRect(): DOMRect;
}

export function decorateStreamLainDropdownPanel(
  menuElement: HTMLElement,
  triggerElement: StreamLainDropdownAnchor,
  options?: StreamLainDropdownPanelOptions
) {
  ensureStreamLainDropdownMenuChromeStyle();
  applyStreamLainMenuTheme(menuElement);
  if (options?.side) {
    menuElement.dataset.streamlainDropdownForcedSide = options.side;
  }
  if (menuElement.dataset.streamlainDropdownDecorated === 'true') {
    positionStreamLainDropdownTail(menuElement, triggerElement);
    return;
  }

  menuElement.dataset.streamlainDropdownDecorated = 'true';
  menuElement.style.opacity = '0';
  menuElement.style.overflow = 'visible';

  let attempts = 0;
  const startEnterAnimation = () => {
    if (!menuElement.isConnected) return;
    const positioned = positionStreamLainDropdownTail(
      menuElement,
      triggerElement
    );
    if (!positioned && attempts++ < 8) {
      requestAnimationFrame(startEnterAnimation);
      return;
    }

    menuElement.style.opacity = '';
    menuElement.classList.add(STREAMLAIN_DROPDOWN_MENU_CLASS);
    trackStreamLainDropdownTail(menuElement, triggerElement);
  };

  requestAnimationFrame(startEnterAnimation);
}

function positionStreamLainDropdownTail(
  menuElement: HTMLElement,
  triggerElement: StreamLainDropdownAnchor
) {
  const forcedSide = menuElement.dataset.streamlainDropdownForcedSide;
  if (forcedSide === 'left' || forcedSide === 'right') {
    return positionStreamLainDropdownTailHorizontal(
      menuElement,
      triggerElement,
      forcedSide
    );
  }

  const menuLeft = Number.parseFloat(menuElement.style.left);
  const menuWidth = menuElement.offsetWidth;
  if (menuWidth <= 0) return false;

  const offsetParent = menuElement.offsetParent;
  const offsetParentLeft =
    offsetParent instanceof HTMLElement
      ? offsetParent.getBoundingClientRect().left
      : (menuElement.parentElement?.getBoundingClientRect().left ?? 0);
  const triggerRect = triggerElement.getBoundingClientRect();
  const menuRect = menuElement.getBoundingClientRect();
  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;
  const menuCenterY = menuRect.top + menuRect.height / 2;
  const side = menuCenterY > triggerCenterY ? 'bottom' : 'top';
  menuElement.setAttribute(DROPDOWN_MENU_SIDE_ATTR, side);
  const menuViewportLeft = Number.isFinite(menuLeft)
    ? offsetParentLeft + menuLeft
    : menuRect.left;
  const minTailInset = getStreamLainDropdownTailMinInset(
    menuElement,
    side === 'bottom' ? 'top' : 'bottom'
  );
  const tailLeft = clampStreamLainDropdownTailOffset(
    triggerCenterX - menuViewportLeft,
    menuWidth,
    minTailInset
  );
  menuElement.style.setProperty(
    DROPDOWN_MENU_TAIL_LEFT_VAR,
    `${Math.round(tailLeft * 100) / 100}px`
  );
  updateStreamLainDropdownTailPath(menuElement);
  return true;
}

function positionStreamLainDropdownTailHorizontal(
  menuElement: HTMLElement,
  triggerElement: StreamLainDropdownAnchor,
  side: StreamLainDropdownPanelSide
) {
  const menuHeight = menuElement.offsetHeight;
  if (menuHeight <= 0) return false;

  // offsetTop/offsetHeight instead of getBoundingClientRect: the enter/exit
  // animation scales the panel, which would distort rect-based math while the
  // tail tracker re-positions during the animation.
  const offsetParent = menuElement.offsetParent;
  const offsetParentTop =
    offsetParent instanceof HTMLElement
      ? offsetParent.getBoundingClientRect().top
      : (menuElement.parentElement?.getBoundingClientRect().top ?? 0);
  const menuViewportTop = offsetParentTop + menuElement.offsetTop;
  const triggerRect = triggerElement.getBoundingClientRect();
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;

  menuElement.setAttribute(DROPDOWN_MENU_SIDE_ATTR, side);
  const minTailInset = getStreamLainDropdownTailMinInset(
    menuElement,
    side === 'right' ? 'left' : 'right'
  );
  const tailTop = clampStreamLainDropdownTailOffset(
    triggerCenterY - menuViewportTop,
    menuHeight,
    minTailInset
  );
  menuElement.style.setProperty(
    DROPDOWN_MENU_TAIL_TOP_VAR,
    `${Math.round(tailTop * 100) / 100}px`
  );
  updateStreamLainDropdownTailPath(menuElement);
  return true;
}

type StreamLainDropdownTailEdge = 'top' | 'right' | 'bottom' | 'left';

function clampStreamLainDropdownTailOffset(
  offset: number,
  panelLength: number,
  minInset: number
) {
  if (panelLength <= minInset * 2) {
    return panelLength / 2;
  }

  return Math.min(Math.max(offset, minInset), panelLength - minInset);
}

function getStreamLainDropdownTailMinInset(
  menuElement: HTMLElement,
  edge: StreamLainDropdownTailEdge
) {
  return (
    DROPDOWN_MENU_TAIL_HALF_WIDTH +
    DROPDOWN_MENU_TAIL_STROKE_WIDTH / 2 +
    getStreamLainDropdownTailCornerClearance(menuElement, edge)
  );
}

function getStreamLainDropdownTailCornerClearance(
  menuElement: HTMLElement,
  edge: StreamLainDropdownTailEdge
) {
  const styles = getComputedStyle(menuElement);
  const radii =
    edge === 'top'
      ? [styles.borderTopLeftRadius, styles.borderTopRightRadius]
      : edge === 'right'
        ? [styles.borderTopRightRadius, styles.borderBottomRightRadius]
        : edge === 'bottom'
          ? [styles.borderBottomLeftRadius, styles.borderBottomRightRadius]
          : [styles.borderTopLeftRadius, styles.borderBottomLeftRadius];

  return Math.max(
    DROPDOWN_MENU_TAIL_CORNER_CLEARANCE,
    ...radii.map(parseStreamLainDropdownCssPixelValue)
  );
}

function parseStreamLainDropdownCssPixelValue(value: string) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function ensureStreamLainDropdownTailElement(menuElement: HTMLElement) {
  const existingTail = menuElement.querySelector<HTMLElement>(
    `.${DROPDOWN_MENU_TAIL_CLASS}`
  );
  if (existingTail) {
    return {
      borderPath: existingTail.querySelector<SVGPathElement>(
        `.${DROPDOWN_MENU_TAIL_BORDER_CLASS}`
      ),
      fillPath: existingTail.querySelector<SVGPathElement>(
        `.${DROPDOWN_MENU_TAIL_FILL_CLASS}`
      ),
      svg: existingTail.querySelector<SVGSVGElement>('svg'),
    };
  }

  const tailElement = document.createElement('div');
  tailElement.className = DROPDOWN_MENU_TAIL_CLASS;

  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const fillPath = document.createElementNS(SVG_NAMESPACE, 'path');
  fillPath.classList.add(DROPDOWN_MENU_TAIL_FILL_CLASS);

  const borderPath = document.createElementNS(SVG_NAMESPACE, 'path');
  borderPath.classList.add(DROPDOWN_MENU_TAIL_BORDER_CLASS);

  svg.append(fillPath, borderPath);
  tailElement.append(svg);
  menuElement.append(tailElement);

  return { borderPath, fillPath, svg };
}

function updateStreamLainDropdownTailPath(menuElement: HTMLElement) {
  const { borderPath, fillPath, svg } =
    ensureStreamLainDropdownTailElement(menuElement);
  if (!borderPath || !fillPath || !svg) return;

  const side = menuElement.getAttribute(DROPDOWN_MENU_SIDE_ATTR);
  if (side === 'right' || side === 'left') {
    updateStreamLainDropdownSideTailPath({
      borderPath,
      fillPath,
      side,
      svg,
    });
    return;
  }

  const tailHeight = DROPDOWN_MENU_TAIL_HEIGHT;
  const strokeOffset = DROPDOWN_MENU_TAIL_STROKE_WIDTH / 2;
  const strokeY = strokeOffset;
  const tipY = tailHeight - strokeOffset;
  const left = strokeOffset;
  const right = DROPDOWN_MENU_TAIL_WIDTH - strokeOffset;
  const center = DROPDOWN_MENU_TAIL_HALF_WIDTH;
  const entryCurveX = 5.6;
  const bendCurveX = 8.4;
  const curveY = 5.2;
  const tipRadius = 1.45;
  const leftTipX = center - tipRadius;
  const rightTipX = center + tipRadius;
  const tipShoulderY = tipY - 1.1;

  svg.setAttribute('viewBox', `0 0 ${DROPDOWN_MENU_TAIL_WIDTH} ${tailHeight}`);
  fillPath.setAttribute(
    'd',
    `M ${left} 0 C ${left + entryCurveX} 0 ${left + bendCurveX} ${curveY} ${leftTipX} ${tipShoulderY} Q ${center} ${tailHeight} ${rightTipX} ${tipShoulderY} C ${right - bendCurveX} ${curveY} ${right - entryCurveX} 0 ${right} 0 Z`
  );
  borderPath.setAttribute(
    'd',
    `M ${left} ${strokeY} C ${left + entryCurveX} ${strokeY} ${left + bendCurveX} ${curveY} ${leftTipX} ${tipShoulderY} Q ${center} ${tipY} ${rightTipX} ${tipShoulderY} C ${right - bendCurveX} ${curveY} ${right - entryCurveX} ${strokeY} ${right} ${strokeY}`
  );
}

function updateStreamLainDropdownSideTailPath({
  borderPath,
  fillPath,
  side,
  svg,
}: {
  borderPath: SVGPathElement;
  fillPath: SVGPathElement;
  side: StreamLainDropdownPanelSide;
  svg: SVGSVGElement;
}) {
  const tailWidth = DROPDOWN_MENU_TAIL_HEIGHT;
  const tailHeight = DROPDOWN_MENU_TAIL_WIDTH;
  const strokeOffset = DROPDOWN_MENU_TAIL_STROKE_WIDTH / 2;
  const top = strokeOffset;
  const bottom = tailHeight - strokeOffset;
  const center = DROPDOWN_MENU_TAIL_HALF_WIDTH;
  const entryCurveY = 5.6;
  const bendCurveY = 8.4;
  const curveInsetX = 5.2;
  const tipRadius = 1.45;
  const upperTipY = center - tipRadius;
  const lowerTipY = center + tipRadius;
  const tipShoulderInsetX = 1.35;

  svg.setAttribute('viewBox', `0 0 ${tailWidth} ${tailHeight}`);

  if (side === 'right') {
    const panelX = tailWidth - strokeOffset;
    const fillPanelX = tailWidth;
    const tipX = strokeOffset;
    const shoulderX = tipShoulderInsetX;
    const curveX = tailWidth - curveInsetX;

    fillPath.setAttribute(
      'd',
      `M ${fillPanelX} 0 C ${fillPanelX} ${entryCurveY} ${curveX} ${bendCurveY} ${shoulderX} ${upperTipY} Q 0 ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${tailHeight - bendCurveY} ${fillPanelX} ${tailHeight - entryCurveY} ${fillPanelX} ${tailHeight} Z`
    );
    borderPath.setAttribute(
      'd',
      `M ${panelX} ${top} C ${panelX} ${top + entryCurveY} ${curveX} ${top + bendCurveY} ${shoulderX} ${upperTipY} Q ${tipX} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${bottom - bendCurveY} ${panelX} ${bottom - entryCurveY} ${panelX} ${bottom}`
    );
    return;
  }

  const panelX = strokeOffset;
  const fillPanelX = 0;
  const tipX = tailWidth - strokeOffset;
  const shoulderX = tailWidth - tipShoulderInsetX;
  const curveX = curveInsetX;

  fillPath.setAttribute(
    'd',
    `M ${fillPanelX} 0 C ${fillPanelX} ${entryCurveY} ${curveX} ${bendCurveY} ${shoulderX} ${upperTipY} Q ${tailWidth} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${tailHeight - bendCurveY} ${fillPanelX} ${tailHeight - entryCurveY} ${fillPanelX} ${tailHeight} Z`
  );
  borderPath.setAttribute(
    'd',
    `M ${panelX} ${top} C ${panelX} ${top + entryCurveY} ${curveX} ${top + bendCurveY} ${shoulderX} ${upperTipY} Q ${tipX} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${bottom - bendCurveY} ${panelX} ${bottom - entryCurveY} ${panelX} ${bottom}`
  );
}

function trackStreamLainDropdownTail(
  menuElement: HTMLElement,
  triggerElement: StreamLainDropdownAnchor
) {
  let frame = 0;
  let rafId = 0;
  let cancelled = false;
  const maxWarmupFrames = 24;

  const queueUpdate = () => {
    if (cancelled) return;
    window.cancelAnimationFrame(rafId);
    rafId = window.requestAnimationFrame(() => {
      if (cancelled || !menuElement.isConnected) return;
      positionStreamLainDropdownTail(menuElement, triggerElement);
      if (frame++ < maxWarmupFrames) {
        queueUpdate();
      }
    });
  };

  const onLayoutChange = () => {
    frame = maxWarmupFrames;
    queueUpdate();
  };

  const cleanup = () => {
    cancelled = true;
    window.cancelAnimationFrame(rafId);
    window.removeEventListener('resize', onLayoutChange);
    window.removeEventListener('scroll', onLayoutChange, true);
  };

  queueUpdate();
  window.addEventListener('resize', onLayoutChange);
  window.addEventListener('scroll', onLayoutChange, true);
  menuElement.addEventListener('streamlain-dropdown-menu-cleanup', cleanup, {
    once: true,
  });
}

export function cleanupStreamLainDropdownPanel(menuElement: HTMLElement) {
  menuElement.dispatchEvent(new Event('streamlain-dropdown-menu-cleanup'));
}

export function animateStreamLainDropdownPanelClose(
  menuElement: HTMLElement,
  onFinished: () => void
) {
  if (menuElement.dataset.streamlainDropdownClosing === 'true') return;
  menuElement.dataset.streamlainDropdownClosing = 'true';
  cleanupStreamLainDropdownPanel(menuElement);

  if (
    !menuElement.isConnected ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    onFinished();
    return;
  }

  const timeout = window.setTimeout(
    onFinished,
    DROPDOWN_MENU_CLOSE_FALLBACK_MS
  );
  menuElement.addEventListener(
    'animationend',
    event => {
      if (event.target !== menuElement) return;
      window.clearTimeout(timeout);
      onFinished();
    },
    { once: true }
  );
  menuElement.classList.remove(STREAMLAIN_DROPDOWN_MENU_CLASS);
  menuElement.classList.add(STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS);
}
